import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { useLazyQuery, useMutation } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";

import { Button, Card, Checkbox, FormGroup, Input, PageHeader } from "@cofano/cck";
import { Grid2 as Grid } from "@mui/material";

import { FormErrorMessage } from "components/form/FormErrorMessage";
import Helmet from "components/helmet/Helmet";
import { FormArrayOnlyRows } from "components/cck/FormArray";
import NumberInput from "components/cck/NumberInput";

import { DataFormat, Language } from "imports/Utils";

import { feetStaffelSchema, FeetStaffelSchemaInput, FeetStaffelSchemaOutput } from "./utils/staffel.schema";

import { StaffelInputType } from "__generated__/enums";
import { GetStaffelDocument } from "__generated__/view/staffels/graphql/staffel.queries.generated";
import { SaveStaffelDocument } from "__generated__/view/staffels/graphql/staffel.mutations.generated";

const FeetStaffelCreateEdit = () => {

	const navigate = useNavigate();
	const { id } = useParams();

	const [ saveFeetStaffel, { loading: mutationLoading } ] = useMutation(SaveStaffelDocument);
	const [ getStaffel, { loading: queryLoading } ] = useLazyQuery(GetStaffelDocument);

	const { control, handleSubmit, reset } = useForm<FeetStaffelSchemaInput, unknown, FeetStaffelSchemaOutput>({
		resolver: zodResolver(feetStaffelSchema),
		defaultValues: {
			id: null,
			description: "",
			staffelItems: [],
		},
	});

	const { fields, append, remove } = useFieldArray({
		control,
		name: "staffelItems",
	});

	const save = (data: FeetStaffelSchemaOutput) => {
		saveFeetStaffel({
			variables: {
				input: {
					type: StaffelInputType.Feet,
					feetStaffel: {
						id: data.id,
						description: data.description,
						staffelItems: data.staffelItems.map(it => ({
							type: StaffelInputType.Feet,
							feetStaffelItem: {
								id: it.id ?? 0,
								startFeet: it.startFeet,
								endFeet: it.endFeet,
								price: it.price,
								reefer: it.reefer,
								empty: it.empty,
							},
						})),
					},
				},
			},
		}).then(({ data }) => {
			navigate(`/view/staffels/${data?.saveStaffel?.id}/details/feet`);
		});
	};

	useEffect(() => {
		if (id) {
			getStaffel({
				variables: {
					id: Number(id),
				} })
				.then((result) => {
					if (result.data?.staffel) {
						reset({
							id: result.data.staffel.id,
							description: result.data.staffel.description ?? "",
							staffelItems: result.data.staffel.staffelItems?.map(it => ({
								id: it.id,
								startFeet: it.__typename === "FeetStaffelItem" ? it.startFeet : null,
								endFeet: it.__typename === "FeetStaffelItem" ? it.endFeet : null,
								price: it.__typename === "FeetStaffelItem" ? it.price : null,
								reefer: it.__typename === "FeetStaffelItem" ? it.reefer ?? false : false,
								empty: it.__typename === "FeetStaffelItem" ? it.empty ?? false : false,
							})) ?? [],
						});
					}
				});
		}
	}, [ id ]);

	return (
		<form onSubmit={handleSubmit(save)}>
			<PageHeader>
				<PageHeader.Title>
					{Language("FEET")}
					<Helmet title={intl.formatMessage({ id: "FEET" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					<Button onClick={() => navigate(id ? `/view/staffels/${id}/details/feet` : "/view/staffels")} variant="outlined">
						{Language("CANCEL")}
					</Button>

					<Button loading={queryLoading || mutationLoading} type="submit">
						{Language("SAVE")}
					</Button>
				</PageHeader.Actions>
			</PageHeader>

			<Grid spacing={3} container>
				<Grid size={6}>
					<Card>
						<FormGroup
							label={intl.formatMessage({ id: "DESCRIPTION" })}
							labelPlacement="left-center"
							overflow="hidden"
							required>
							<Controller
								name="description"
								control={control}
								render={({ field, formState }) => (
									<React.Fragment>
										<Input {...field} autoFocus />
										<FormErrorMessage name={field.name} errors={formState.errors} />
									</React.Fragment>
								)} />
						</FormGroup>

						<FormArrayOnlyRows
							array={fields}
							fields={[ {
								title: intl.formatMessage({ id: "FEET_START" }),
								required: true,
								render: index => (
									<Controller
										name={`staffelItems.${index}.startFeet`}
										control={control}
										render={({ field, formState }) => (
											<React.Fragment>
												<NumberInput {...field} decimalScale={0} addonAfter={DataFormat.UNITS.FEET} />
												<FormErrorMessage name={field.name} errors={formState.errors} />
											</React.Fragment>
										)} />
								),
							}, {
								title: intl.formatMessage({ id: "FEET_END" }),
								required: true,
								render: index => (
									<Controller
										name={`staffelItems.${index}.endFeet`}
										control={control}
										render={({ field, formState }) => (
											<React.Fragment>
												<NumberInput {...field} decimalScale={0} addonAfter={DataFormat.UNITS.FEET} />
												<FormErrorMessage name={field.name} errors={formState.errors} />
											</React.Fragment>
										)} />
								),
							}, {
								title: intl.formatMessage({ id: "PRICE" }),
								required: true,
								render: index => (
									<Controller
										name={`staffelItems.${index}.price`}
										control={control}
										render={({ field, formState }) => (
											<React.Fragment>
												<NumberInput {...field} />
												<FormErrorMessage name={field.name} errors={formState.errors} />
											</React.Fragment>
										)} />
								),
							}, {
								title: intl.formatMessage({ id: "REEFER" }),
								render: index => (
									<Controller
										name={`staffelItems.${index}.reefer`}
										control={control}
										render={({ field }) => (
											<Checkbox
												value={undefined}
												onChange={field.onChange}
												checked={field.value} />
										)} />
								),
							}, {
								title: intl.formatMessage({ id: "EMPTY" }),
								render: index => (
									<Controller
										name={`staffelItems.${index}.empty`}
										control={control}
										render={({ field }) => (
											<Checkbox
												value={undefined}
												onChange={field.onChange}
												checked={field.value} />
										)}
									/>
								),
							} ]}
							onAdd={() => {
								append({
									id: null,
									startFeet: 0,
									endFeet: 0,
									price: 0,
									reefer: false,
									empty: false,
								});
							}}
							onDelete={remove} />
					</Card>
				</Grid>
			</Grid>
		</form>
	);
};

export default FeetStaffelCreateEdit;
